import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { channelQuotaKey, createPixCharge, fetchPixCharge } from '../api/billing';
import type { PixCharge, QuotaChannel } from '../model/ledger';
import { PixQr } from './pix-qr';

const CHANNELS: Array<{ value: QuotaChannel; label: string }> = [
  { value: 'email', label: 'E-mail' },
  { value: 'sms', label: 'SMS' },
];

export function RechargeDialog({
  open,
  onOpenChange,
  passbookId,
  channel,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  passbookId: string;
  channel?: QuotaChannel;
}) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<QuotaChannel>(channel ?? 'email');
  const [slots, setSlots] = useState('1000');
  const [busy, setBusy] = useState(false);
  const [charge, setCharge] = useState<PixCharge | null>(null);
  const [blocked, setBlocked] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSelected(channel ?? 'email');
    setSlots(channel === 'sms' ? '100' : '1000');
    setCharge(null);
    setBlocked(null);
    setBusy(false);
  }, [channel, open]);

  const poll = useQuery({
    queryKey: ['pix-charge', charge?.id],
    enabled: open && charge?.status === 'pending',
    queryFn: () => fetchPixCharge(charge!.id),
    refetchInterval: 4000,
  });

  useEffect(() => {
    const next = poll.data?.charge;
    if (!next || !charge || next.id !== charge.id) return;
    setCharge(next);
    if (next.status === 'paid') {
      void queryClient.invalidateQueries({ queryKey: channelQuotaKey(passbookId) });
      toast.success('Pagamento confirmado. A cota foi creditada.');
    }
  }, [charge, passbookId, poll.data, queryClient]);

  const generate = async () => {
    const amount = Math.floor(Number(slots));
    if (!Number.isFinite(amount) || amount < 1) {
      toast.error('Informe quantos slots quer recarregar.');
      return;
    }
    setBusy(true);
    setBlocked(null);
    try {
      const response = await createPixCharge({ passbookId, channel: selected, slots: amount });
      if (!response.charge || response.status === 'unavailable') {
        setBlocked(response.message);
        setCharge(null);
        return;
      }
      setCharge(response.charge);
      if (response.charge.status === 'failed') {
        setBlocked(response.message);
      }
    } catch (err) {
      setBlocked(err instanceof Error ? err.message : 'Não foi possível gerar a cobrança.');
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!charge?.copiaECola) return;
    try {
      await navigator.clipboard.writeText(charge.copiaECola);
      toast.success('Código PIX copiado.');
    } catch {
      toast.error('Não foi possível copiar. Selecione o código e copie manualmente.');
    }
  };

  const waiting = charge?.status === 'pending';
  const qrImage = charge?.qrCodeBase64?.startsWith('data:') ? charge.qrCodeBase64 : null;
  // O Bling v3 devolve `linkQRCodePix` (página dele, não imagem): abre em outra aba.
  const qrLink = charge?.qrCodeBase64?.startsWith('http') ? charge.qrCodeBase64 : null;
  const qrValue = qrImage || qrLink ? null : charge?.copiaECola;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Recarga avulsa</DialogTitle>
          <DialogDescription>
            Gera uma cobrança PIX no Bling. A cota só aumenta depois da confirmação do pagamento.
          </DialogDescription>
        </DialogHeader>

        {!charge || charge.status === 'failed' ? (
          <div className="space-y-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="recharge-channel">Canal</Label>
              <div className="flex gap-2">
                {CHANNELS.map((item) => (
                  <Button
                    key={item.value}
                    type="button"
                    variant={selected === item.value ? 'primary' : 'secondary'}
                    onClick={() => setSelected(item.value)}
                    disabled={Boolean(channel)}
                  >
                    {item.label}
                  </Button>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="recharge-slots">Slots</Label>
              <Input
                id="recharge-slots"
                inputMode="numeric"
                value={slots}
                onChange={(event) => setSlots(event.target.value.replace(/[^\d]/g, ''))}
              />
            </div>
          </div>
        ) : null}

        {blocked ? (
          <p className="rounded-xl border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm" role="status">
            {blocked}
          </p>
        ) : null}

        {charge && charge.status !== 'failed' ? (
          <div className="space-y-3">
            <p className="text-sm" role="status">
              {charge.message}
            </p>
            {charge.copiaECola ? (
              <>
                <div className="flex gap-2">
                  <Input readOnly value={charge.copiaECola} aria-label="PIX copia e cola" />
                  <Button type="button" variant="secondary" onClick={() => void copy()} aria-label="Copiar PIX">
                    <Copy className="size-4" />
                  </Button>
                </div>
                {qrValue ? <PixQr value={qrValue} /> : null}
              </>
            ) : null}
            {qrImage ? (
              <img src={qrImage} alt="QR Code PIX" className="mx-auto h-52 w-52 rounded-xl bg-white object-contain p-2" />
            ) : null}
            {qrLink ? (
              <Button asChild variant="secondary" className="w-full">
                <a href={qrLink} target="_blank" rel="noreferrer">
                  Abrir o PIX (QR Code) no Bling
                </a>
              </Button>
            ) : null}
            {waiting ? <p className="text-sm text-muted-foreground">Aguardando confirmação do Bling. Esta tela consulta o status sozinha.</p> : null}
            {charge.status === 'paid' ? <p className="text-sm font-medium">Cota creditada.</p> : null}
            {charge.status === 'expired' ? <p className="text-sm">A cobrança expirou. A cota não mudou.</p> : null}
          </div>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
          {!charge || charge.status === 'failed' ? (
            <Button type="button" onClick={() => void generate()} loading={busy}>
              Gerar cobrança
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
